import { ResponseError } from "@wingspanhq/wingspan-api-sdk";
import {
  useWSQuery,
  WSQueryConfig,
  WSQueryKey,
  WSQueryResult
} from "@ws-react-query";

/**
 * The shape every typescript-fetch SDK endpoint method shares: a single request
 * object (which may be optional, e.g. `listAccounts`) plus optional fetch init
 * overrides, resolving to the response model.
 */
export type SdkEndpointMethod = (
  request: any,
  initOverrides?: any
) => Promise<any>;

/**
 * Keys of `TApi` that are real endpoint calls — the clean `xxx` methods, not the
 * generated `xxxRaw` / `xxxRequestOpts` companions. Used to constrain (and
 * autocomplete) the `methodName` argument of {@link createV3QueryHook}.
 */
export type V3QueryMethodName<TApi> = {
  [K in keyof TApi]: K extends `${string}Raw` | `${string}RequestOpts`
    ? never
    : TApi[K] extends SdkEndpointMethod
      ? K
      : never;
}[keyof TApi];

/**
 * Hook arguments, mirroring the SDK method 1:1. When the SDK request is optional
 * (e.g. `listAccounts`) the hook's `request` stays optional; when it is required
 * (e.g. `getAccount`) the hook requires it — matching the v3 SDK-mirror rule.
 */
type V3QueryHookArgs<TRequest, TResponse, TData> = undefined extends TRequest
  ? [
      request?: TRequest,
      config?: WSQueryConfig<TResponse, ResponseError, TData>
    ]
  : [
      request: TRequest,
      config?: WSQueryConfig<TResponse, ResponseError, TData>
    ];

/**
 * The query hook produced by {@link createV3QueryHook}. Generic over `TData` so a
 * `config.select` transform is fully typed; `TData` defaults to the SDK response
 * type, so without `select` the hook returns the response unchanged.
 */
export type V3QueryHook<TRequest, TResponse> = <TData = TResponse>(
  ...args: V3QueryHookArgs<TRequest, TResponse, TData>
) => WSQueryResult<TData, ResponseError>;

/** Resolves the typed hook for a given SDK method type. */
type V3QueryHookFor<TMethod> = TMethod extends (
  request: infer TRequest,
  ...rest: any[]
) => Promise<infer TResponse>
  ? V3QueryHook<TRequest, TResponse>
  : never;

/**
 * Build a fully-typed React Query hook from an SDK API instance + method name.
 *
 * The generated hook mirrors the SDK call 1:1 — same request type and
 * optionality, same response type — forwards a `WSQueryConfig`, and uses the
 * `[queryKey, request]` cache-key shape. That is exactly the v3 query convention
 * (see `docs/query-management.md` / the v1→v3 migration skill), produced with
 * zero per-hook boilerplate and no hand-written `useWSQuery` call.
 *
 * @param api        An instantiated SDK API client, e.g. `accountsApi`, `personsApi`.
 * @param methodName A read method on `api` (`getAccount`, `listAccounts`, …).
 *                   Only the clean endpoint methods are offered — `*Raw` and
 *                   `*RequestOpts` are excluded.
 * @param queryKey   The cache key. A `string` becomes `[queryKey, request]`; an
 *                   array is spread, becoming `[...queryKey, request]`.
 *
 * @example
 * // src/v3/query/platform/account/queries/useQueryAccount.ts
 * import { accountsApi } from "@api";
 * export const QUERY_V3_ACCOUNT = "QUERY_V3_ACCOUNT";
 * export const useQueryAccount = createV3QueryHook(
 *   accountsApi,
 *   "getAccount",
 *   QUERY_V3_ACCOUNT
 * );
 * // → useQueryAccount(request: AccountsApiGetAccountRequest, config?)
 * //     : WSQueryResult<Account, ResponseError>
 *
 * @example
 * // optional-request list endpoint — `request` stays optional, just like the SDK
 * export const QUERY_V3_ACCOUNTS = "QUERY_V3_ACCOUNTS";
 * export const useQueryAccounts = createV3QueryHook(
 *   accountsApi,
 *   "listAccounts",
 *   QUERY_V3_ACCOUNTS
 * );
 * // → useQueryAccounts(request?: AccountsApiListAccountsRequest, config?)
 * //     : WSQueryResult<AccountList, ResponseError>
 *
 * @example
 * // `config.select` is fully typed off the response model
 * const queryAccount = useQueryAccount(
 *   { accountId },
 *   { select: account => account.profile, enabled: !!accountId }
 * );
 * // queryAccount.data: AccountProfile | undefined
 */
export function createV3QueryHook<
  TApi extends object,
  TName extends V3QueryMethodName<TApi>
>(
  api: TApi,
  methodName: TName,
  queryKey: WSQueryKey
): V3QueryHookFor<TApi[TName]> {
  const baseKey = Array.isArray(queryKey) ? queryKey : [queryKey];
  const callEndpoint = (api[methodName] as unknown as SdkEndpointMethod).bind(
    api
  );

  const useGeneratedQuery = (
    request?: unknown,
    config?: WSQueryConfig<unknown, ResponseError>
  ): WSQueryResult<unknown, ResponseError> =>
    useWSQuery<unknown, ResponseError>(
      [...baseKey, request],
      () => callEndpoint(request),
      config
    );

  return useGeneratedQuery as unknown as V3QueryHookFor<TApi[TName]>;
}
